import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { LoyaltyRewardOption } from "@lemmonhq/orders";
import type {
  CheckoutData,
  LoyaltyRewardSelection,
  Service,
} from "@lemmonhq/types";
import type { CheckoutCustomerPreview } from "./types";

type Props = {
  preview: CheckoutCustomerPreview;
  options: LoyaltyRewardOption[];
  selected: LoyaltyRewardSelection[];
  currency: string;
  checkout: CheckoutData;
  services: Service[];
  maximumValue: number;
  onChange: (selections: LoyaltyRewardSelection[]) => void;
};

export const RewardsPanel = defineComponent<Props>({
  props: [
    "preview",
    "options",
    "selected",
    "currency",
    "checkout",
    "services",
    "maximumValue",
    "onChange",
  ],
  setup(props) {
    const { t } = useLocalization();
    const expandedCategoryRewards = ref<string[]>([]);
    const money = (n: number) => `${props.currency} ${n.toFixed(2)}`;
    const couponMask =
      "radial-gradient(circle at left center, transparent 10px, black 10.5px), radial-gradient(circle at right center, transparent 10px, black 10.5px)";

    const selectedFor = (promotionId: string) =>
      props.selected.find(
        (selection) => selection.promotion_id === promotionId,
      );
    const replaceSelection = (next: LoyaltyRewardSelection) =>
      props.onChange([
        ...props.selected.filter(
          (selection) => selection.promotion_id !== next.promotion_id,
        ),
        next,
      ]);
    const replaceOptionSelection = (
      option: LoyaltyRewardOption,
      next: LoyaltyRewardSelection,
    ) =>
      props.onChange([
        ...props.selected.filter((selection) => {
          if (selection.promotion_id === next.promotion_id) return false;
          if (!option.promotion.coin_program_id) return true;
          const selectedPromotion = props.options.find(
            (candidate) => candidate.promotion.id === selection.promotion_id,
          )?.promotion;
          return (
            selectedPromotion?.coin_program_id !==
            option.promotion.coin_program_id
          );
        }),
        next,
      ]);
    const toggle = (option: LoyaltyRewardOption) => {
      const promotion = option.promotion;
      const current = selectedFor(promotion.id);
      props.onChange(
        current
          ? props.selected.filter(
              (selection) => selection.promotion_id !== promotion.id,
            )
          : [
              ...props.selected.filter((selection) => {
                if (!promotion.coin_program_id) return true;
                const selectedPromotion = props.options.find(
                  (candidate) =>
                    candidate.promotion.id === selection.promotion_id,
                )?.promotion;
                return (
                  selectedPromotion?.coin_program_id !==
                  promotion.coin_program_id
                );
              }),
              { promotion_id: promotion.id, quantity: 1 },
            ],
      );
    };
    const categoryChoices = (
      option: LoyaltyRewardOption,
    ): {
      key: string;
      productId: number;
      cartItemId?: string;
      orderItemId?: number;
      name: string;
      quantity: number;
    }[] => {
      if (option.promotion.reward.type !== "product_category") return [];
      const categoryIds = new Set(
        option.promotion.reward.categories.map((category) => category.id),
      );
      const productIds = new Set(
        props.services.flatMap((service) =>
          (service.categories ?? [])
            .filter((category) => categoryIds.has(category.id))
            .flatMap((category) => category.product_ids ?? []),
        ),
      );
      if (props.checkout.payload?.type === "cart")
        return props.checkout.payload.object.items
          .filter((item) => productIds.has(item.productId))
          .map((item) => ({
            key: `cart:${item.cartId}`,
            productId: item.productId,
            cartItemId: item.cartId,
            name: item.name,
            quantity: item.quantity,
          }));
      if (props.checkout.payload?.type === "table") {
        const table = props.checkout.payload.object;
        if (table.type === "split") return [];
        const partial =
          table.type === "partial"
            ? new Map(table.items.map((item) => [item.id, item.quantity]))
            : null;
        return table.orders.flatMap((order) =>
          (order.items ?? [])
            .filter((item) => productIds.has(item.product_id))
            .map((item) => ({
              key: `order:${item.id}`,
              productId: item.product_id,
              orderItemId: item.id,
              name: item.product?.name ?? `#${item.product_id}`,
              quantity: partial
                ? (partial.get(item.id) ?? 0)
                : Math.max(0, item.quantity - (item.paid_quantity ?? 0)),
            }))
            .filter((item) => item.quantity > 0),
        );
      }
      return [];
    };

    const fixedProductQuantity = (option: LoyaltyRewardOption) => {
      if (option.promotion.reward.type !== "product") return Infinity;
      const reward = option.promotion.reward;
      const categoryIds = option.promotion.requirements.flatMap(
        (requirement) =>
          requirement.type === "product_category"
            ? requirement.category_ids
            : [],
      );
      const allowed = new Set(
        reward.product_id != null
          ? [reward.product_id]
          : [
              ...option.promotion.requirements.flatMap((requirement) =>
                requirement.type === "product" ? requirement.product_ids : [],
              ),
              ...props.services.flatMap((service) =>
                (service.categories ?? [])
                  .filter((category) => categoryIds.includes(category.id))
                  .flatMap((category) => category.product_ids ?? []),
              ),
            ],
      );
      if (props.checkout.payload?.type === "cart")
        return props.checkout.payload.object.items
          .filter((item) => allowed.has(item.productId))
          .reduce((sum, item) => sum + item.quantity, 0);
      if (props.checkout.payload?.type === "table") {
        const table = props.checkout.payload.object;
        if (table.type === "split") return 0;
        const partial =
          table.type === "partial"
            ? new Map(table.items.map((item) => [item.id, item.quantity]))
            : null;
        return table.orders
          .flatMap((order) => order.items ?? [])
          .filter((item) => allowed.has(item.product_id))
          .reduce(
            (sum, item) =>
              sum +
              (partial
                ? (partial.get(item.id) ?? 0)
                : Math.max(0, item.quantity - (item.paid_quantity ?? 0))),
            0,
          );
      }
      return 0;
    };

    const categoryChoiceQuantity = (
      selection: LoyaltyRewardSelection | undefined,
      choice: ReturnType<typeof categoryChoices>[number],
    ) =>
      selection?.product_choices?.find(
        (selectedChoice) =>
          selectedChoice.product_id === choice.productId &&
          (selectedChoice.cart_item_id === choice.cartItemId ||
            selectedChoice.order_item_id === choice.orderItemId),
      )?.quantity ?? 0;

    const selectedCategoryItems = (
      selection: LoyaltyRewardSelection | undefined,
    ) =>
      selection?.product_choices?.reduce(
        (sum, choice) => sum + choice.quantity,
        0,
      ) ?? 0;

    const updateCategoryChoice = (
      option: LoyaltyRewardOption,
      choice: ReturnType<typeof categoryChoices>[number],
      delta: number,
      maximumItems: number,
    ) => {
      const promotion = option.promotion;
      if (promotion.reward.type !== "product_category") return;
      const selection = selectedFor(promotion.id);
      const choices = [...(selection?.product_choices ?? [])];
      const index = choices.findIndex(
        (selectedChoice) =>
          selectedChoice.product_id === choice.productId &&
          (selectedChoice.cart_item_id === choice.cartItemId ||
            selectedChoice.order_item_id === choice.orderItemId),
      );
      const current = index >= 0 ? choices[index].quantity : 0;
      const total = selectedCategoryItems(selection);
      const nextQuantity =
        delta > 0
          ? Math.max(
              0,
              Math.min(
                choice.quantity,
                current + Math.min(delta, maximumItems - total),
              ),
            )
          : Math.max(0, current + delta);
      if (index >= 0) {
        if (nextQuantity > 0)
          choices[index] = { ...choices[index], quantity: nextQuantity };
        else choices.splice(index, 1);
      } else if (nextQuantity > 0) {
        choices.push({
          product_id: choice.productId,
          cart_item_id: choice.cartItemId,
          order_item_id: choice.orderItemId,
          quantity: nextQuantity,
        });
      }
      const nextTotal = choices.reduce(
        (sum, selectedChoice) => sum + selectedChoice.quantity,
        0,
      );
      if (nextTotal === 0) {
        props.onChange(
          props.selected.filter(
            (selected) => selected.promotion_id !== promotion.id,
          ),
        );
        return;
      }
      replaceOptionSelection(option, {
        promotion_id: promotion.id,
        quantity: Math.ceil(nextTotal / promotion.reward.quantity),
        product_choices: choices,
      });
    };

    const renderOption = (option: LoyaltyRewardOption) => {
      const promotion = option.promotion;
      const selection = selectedFor(promotion.id);
      const checked = !!selection;
      const selectedQuantity = selection?.quantity ?? 1;
      const canSelect =
        checked || !option.reason || option.reason === "coin_conflict";
      const membership = props.preview.memberships?.find(
        (item) => item.program_id === promotion.membership_program_id,
      );
      const reward = promotion.reward;
      const categoryExpanded =
        reward.type === "product_category" &&
        expandedCategoryRewards.value.includes(promotion.id);
      const description =
        reward.type === "order_discount" || reward.type === "product_discount"
          ? `${reward.mode === "percentage" ? `${reward.value}%` : money(reward.value)} ${t(
              reward.type === "order_discount"
                ? "loyalty.rewards.off_order"
                : "loyalty.rewards.off_items",
            )}`
          : undefined;
      const rewardShort =
        reward.type === "order_discount" || reward.type === "product_discount"
          ? reward.mode === "percentage"
            ? `${reward.value}%`
            : money(reward.value)
          : reward.type === "product" || reward.type === "product_category"
            ? `${reward.quantity}×`
            : option.value > 0
              ? money(option.value)
              : "—";
      const rewardLabel = promotion.coin_program_id
        ? t("loyalty.rewards.coin_rewards")
        : t("loyalty.rewards.membership_rewards");
      const finite =
        membership?.cap_count != null || membership?.cap_amount != null;
      const categoryDescription =
        reward.type === "product_category"
          ? `${reward.quantity} ${t("loyalty.rewards.free_item_from")} ${reward.categories
              .map((category) => category.name)
              .join(", ")}`
          : undefined;
      const coinMaximum = promotion.requirements
        .filter((requirement) => requirement.type === "coin")
        .reduce(
          (maximum, requirement) =>
            Math.min(
              maximum,
              Math.floor(
                (props.preview.coin_balances[requirement.coin_program_id] ??
                  0) / requirement.amount,
              ),
            ),
          Infinity,
        );
      const valueMaximum =
        reward.type === "order_discount" && reward.mode === "absolute"
          ? Math.ceil(props.maximumValue / reward.value)
          : Infinity;
      const productMaximum =
        reward.type === "product"
          ? Math.floor(fixedProductQuantity(option) / reward.quantity)
          : Infinity;
      const maximumQuantity = Math.max(
        1,
        Math.min(coinMaximum, valueMaximum, productMaximum),
      );
      const categoryItems =
        reward.type === "product_category"
          ? selectedCategoryItems(selection)
          : 0;
      const maximumCategoryItems =
        reward.type === "product_category"
          ? Math.max(0, coinMaximum * reward.quantity)
          : 0;
      const completeCategoryItems =
        reward.type === "product_category" &&
        categoryItems > 0 &&
        categoryItems % reward.quantity === 0;

      return (
        <div
          key={promotion.id}
          class={`relative transition-all duration-200 ${
            canSelect ? "cursor-pointer" : "cursor-not-allowed opacity-50"
          }`}
          style={{ padding: "0 10px" }}
          onClick={() => {
            if (!canSelect) return;
            if (reward.type !== "product_category") {
              toggle(option);
              return;
            }
            expandedCategoryRewards.value = categoryExpanded
              ? expandedCategoryRewards.value.filter(
                  (id) => id !== promotion.id,
                )
              : [...expandedCategoryRewards.value, promotion.id];
          }}
        >
          <button
            type="button"
            disabled={!canSelect}
            aria-pressed={checked}
            aria-expanded={
              reward.type === "product_category" ? categoryExpanded : undefined
            }
            class={`flex w-full overflow-hidden rounded-2xl text-left shadow-md transition-all duration-200 ${
              checked
                ? "ring-2 ring-primary shadow-primary/30"
                : "shadow-base-300/40"
            }`}
            style={{
              maskImage: couponMask,
              WebkitMaskImage: couponMask,
              maskSize: "51% 100%",
              WebkitMaskSize: "51% 100%",
              maskPosition: "left top, right top",
              WebkitMaskPosition: "left top, right top",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
            }}
          >
            <span class="flex w-20 shrink-0 flex-col items-center justify-center bg-primary px-2 py-3 text-primary-content sm:w-24 sm:px-3">
              <span class="w-full truncate text-center text-lg font-black leading-none sm:text-xl">
                {rewardShort}
              </span>
              <span class="mt-0.5 w-full truncate text-center text-[8px] font-bold uppercase tracking-wider opacity-80">
                {rewardLabel}
              </span>
            </span>

            <span class="w-px shrink-0 border-l border-dashed border-accent-content/30 bg-accent" />

            <span class="flex min-w-0 flex-1 items-center gap-4 bg-accent px-4 py-3 text-accent-content">
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-bold leading-tight">
                  {promotion.name}
                </span>
                {description && (
                  <span class="mt-0.5 block text-xs leading-snug opacity-70">
                    {description}
                  </span>
                )}
                {categoryDescription && (
                  <span class="mt-0.5 block text-xs leading-snug opacity-70">
                    {categoryDescription}
                  </span>
                )}
                {membership && membership.name !== promotion.name && (
                  <span class="mt-1 block text-[10px] opacity-60">
                    {membership.name}
                  </span>
                )}
                {promotion.requirements
                  .filter((requirement) => requirement.type === "coin")
                  .map((requirement) => (
                    <span class="mt-1.5 inline-flex items-center gap-1 rounded-full bg-accent-content/10 px-2 py-0.5 text-[11px] font-semibold">
                      {requirement.amount} {t("loyalty.rewards.coin_units")} ·{" "}
                      {t("loyalty.rewards.balance")}:{" "}
                      {props.preview.coin_balances[
                        requirement.coin_program_id
                      ] ?? 0}
                      {checked && !option.reason
                        ? ` → ${
                            (props.preview.coin_balances[
                              requirement.coin_program_id
                            ] ?? 0) -
                            requirement.amount * selectedQuantity
                          }`
                        : ""}
                    </span>
                  ))}
                {finite && (
                  <span class="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] opacity-60">
                    {membership?.cap_count != null && (
                      <span>
                        {t("loyalty.modal.uses_left")}:{" "}
                        {membership.remaining ?? 0}
                        {checked && !option.reason
                          ? ` → ${Math.max(0, (membership.remaining ?? 0) - 1)}`
                          : ""}
                      </span>
                    )}
                    {membership?.cap_amount != null && (
                      <span>
                        {t("loyalty.modal.budget_left")}:{" "}
                        {money(membership.budget_remaining ?? 0)}
                        {checked && !option.reason
                          ? ` → ${money(
                              Math.max(
                                0,
                                (membership.budget_remaining ?? 0) -
                                  option.value,
                              ),
                            )}`
                          : ""}
                      </span>
                    )}
                    {membership?.cap_period && (
                      <span>
                        {t(`loyalty.rewards.period_${membership.cap_period}`)}
                      </span>
                    )}
                  </span>
                )}
              </span>

              <span class="flex shrink-0 flex-col items-end gap-0.5 text-right">
                {option.value > 0 && (
                  <span class="text-lg font-black leading-none tabular-nums sm:text-xl">
                    −{money(option.value)}
                  </span>
                )}
                <span
                  class={`text-[10px] font-bold uppercase tracking-wide ${
                    option.reason
                      ? "text-error"
                      : checked
                        ? "text-primary"
                        : "opacity-60"
                  }`}
                >
                  {option.reason
                    ? t(`loyalty.rewards.${option.reason}`)
                    : reward.type === "product_category" && checked
                      ? `${categoryItems} ${t("loyalty.rewards.items_selected")}`
                      : t(
                          checked
                            ? "loyalty.rewards.selected"
                            : "loyalty.rewards.select",
                        )}
                </span>
              </span>

              <span
                class={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200 ${
                  checked
                    ? "border-primary bg-primary text-primary-content"
                    : "border-accent-content/30"
                }`}
              >
                {reward.type === "product_category" ? (
                  <span class="material-symbols-outlined text-base">
                    {categoryExpanded ? "expand_less" : "expand_more"}
                  </span>
                ) : checked ? (
                  <span class="material-symbols-outlined text-base">check</span>
                ) : null}
              </span>
            </span>
          </button>
          {checked &&
            !!promotion.coin_program_id &&
            ((reward.type === "order_discount" && reward.mode === "absolute") ||
              reward.type === "product") && (
              <div
                class="mt-2 flex items-center justify-end gap-2 rounded-xl bg-base-200 px-3 py-2"
                onClick={(event) => event.stopPropagation()}
              >
                <span class="mr-auto text-xs text-base-content/60">
                  {t("loyalty.rewards.quantity")}
                </span>
                <button
                  type="button"
                  class="btn btn-circle btn-ghost btn-xs"
                  disabled={selectedQuantity <= 1}
                  onClick={() =>
                    replaceSelection({
                      ...selection!,
                      quantity: selectedQuantity - 1,
                    })
                  }
                >
                  −
                </button>
                <span class="min-w-8 text-center text-sm font-bold tabular-nums">
                  {selectedQuantity}
                </span>
                <button
                  type="button"
                  class="btn btn-circle btn-ghost btn-xs"
                  disabled={selectedQuantity >= maximumQuantity}
                  onClick={() =>
                    replaceSelection({
                      ...selection!,
                      quantity: selectedQuantity + 1,
                    })
                  }
                >
                  +
                </button>
              </div>
            )}
          {categoryExpanded && reward.type === "product_category" && (
            <div
              class="mt-2 space-y-2 rounded-xl border border-base-300 bg-base-200 px-3 py-3"
              onClick={(event) => event.stopPropagation()}
            >
              <p class="text-xs font-semibold text-base-content/60">
                {t("loyalty.rewards.choose_items")}
              </p>
              {categoryChoices(option).map((choice) => {
                const quantity = categoryChoiceQuantity(selection, choice);
                return (
                  <div
                    key={choice.key}
                    class="flex items-center gap-3 rounded-lg bg-base-100 px-3 py-2"
                  >
                    <span class="min-w-0 flex-1 truncate text-sm font-medium">
                      {choice.name}
                    </span>
                    <span class="text-xs text-base-content/50">
                      {t("loyalty.rewards.in_cart")}: {choice.quantity}
                    </span>
                    <button
                      type="button"
                      class="btn btn-circle btn-ghost btn-xs"
                      disabled={quantity <= 0}
                      aria-label={`${choice.name} −`}
                      onClick={() =>
                        updateCategoryChoice(
                          option,
                          choice,
                          -1,
                          maximumCategoryItems,
                        )
                      }
                    >
                      −
                    </button>
                    <span class="min-w-6 text-center text-sm font-bold tabular-nums">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      class="btn btn-circle btn-ghost btn-xs"
                      disabled={
                        quantity >= choice.quantity ||
                        categoryItems >= maximumCategoryItems
                      }
                      aria-label={`${choice.name} +`}
                      onClick={() =>
                        updateCategoryChoice(
                          option,
                          choice,
                          1,
                          maximumCategoryItems,
                        )
                      }
                    >
                      +
                    </button>
                  </div>
                );
              })}
              <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-base-300 pt-2 text-xs">
                <span class="font-semibold">
                  {categoryItems} {t("loyalty.rewards.items_selected")}
                  {reward.quantity > 1 && !completeCategoryItems
                    ? ` · ${categoryItems % reward.quantity}/${reward.quantity}`
                    : ""}
                </span>
                {promotion.requirements
                  .filter((requirement) => requirement.type === "coin")
                  .map((requirement) => {
                    const balance =
                      props.preview.coin_balances[
                        requirement.coin_program_id
                      ] ?? 0;
                    const spend =
                      requirement.amount *
                      Math.floor(categoryItems / reward.quantity);
                    return (
                      <span class="text-base-content/60">
                        {t("loyalty.rewards.spend")} {spend} ·{" "}
                        {t("loyalty.rewards.remaining")} {balance - spend}
                      </span>
                    );
                  })}
                {option.value > 0 && (
                  <span class="font-semibold text-success">
                    {t("loyalty.rewards.savings")} {money(option.value)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      );
    };

    return () => (
      <div class="space-y-6">
        {props.options.length === 0 && (
          <p class="py-6 text-center text-sm text-base-content/60">
            {t("loyalty.modal.no_active_rewards")}
          </p>
        )}
        {([false, true] as const).map((coins) => {
          const options = props.options.filter(
            (option) => !!option.promotion.coin_program_id === coins,
          );
          const available = options.filter(
            (option) => !option.reason || !!selectedFor(option.promotion.id),
          );
          const unavailable = options.filter(
            (option) => option.reason && !selectedFor(option.promotion.id),
          );

          return options.length ? (
            <section class="space-y-3">
              <h3 class="flex items-center gap-2 px-1 text-xs font-bold uppercase tracking-widest text-base-content/40">
                {t(
                  coins
                    ? "loyalty.rewards.coin_rewards"
                    : "loyalty.rewards.membership_rewards",
                )}
                <span class="font-normal tabular-nums">{options.length}</span>
              </h3>
              {available.length > 0 && (
                <div class="space-y-3">{available.map(renderOption)}</div>
              )}
              {unavailable.length > 0 && (
                <details class="group rounded-2xl bg-base-200/50 px-3 py-2">
                  <summary class="flex cursor-pointer list-none items-center gap-2 py-1 text-sm font-medium text-base-content/60">
                    <span class="material-symbols-outlined text-base transition-transform group-open:rotate-90">
                      chevron_right
                    </span>
                    {t("loyalty.rewards.unavailable")} ({unavailable.length})
                  </summary>
                  <div class="mt-3 space-y-3 pb-2">
                    {unavailable.map(renderOption)}
                  </div>
                </details>
              )}
            </section>
          ) : null;
        })}
        {props.options.some(
          (option) =>
            option.promotion.coin_program_id ||
            props.preview.memberships?.some(
              (membership) =>
                membership.program_id ===
                  option.promotion.membership_program_id &&
                (membership.cap_count != null || membership.cap_amount != null),
            ),
        ) && (
          <p class="px-1 text-xs leading-relaxed text-base-content/50">
            {t("loyalty.rewards.payment_note")}
          </p>
        )}
      </div>
    );
  },
});
